/**
 * Alshallal Pastry Company HR System - My Leave Page
 * Phase 4: Leave Management
 * Phase 24.2: Enhanced with Request Timeline view
 * Employee view for leave balances and requests
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { canCancelRequest, leaveDisplayStatus } from '@/lib/requestLifecycle';
import { InsufficientBalanceNotice, MyBalanceExceptions } from '@/components/leave/BalanceExceptions';
import { MyReviewCertificates } from '@/components/leave/ReviewCertificates';
import { format } from 'date-fns';
import {
  Calendar,
  Plus,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  FileText,
  Loader2,
  Eye,
  Paperclip,
  Ban
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import RequestTimeline from '@/components/requests/RequestTimeline';
import {
  LeaveAttachmentList,
  LeaveAttachmentUploader,
} from '@/components/leave/LeaveAttachments';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_STYLES = {
  pending_manager: { bg: 'bg-amber-100 text-amber-800', icon: Clock, label: { en: 'Pending Manager', ar: 'بانتظار المدير' } },
  pending_hr: { bg: 'bg-blue-100 text-blue-800', icon: Clock, label: { en: 'Pending HR', ar: 'بانتظار الموارد البشرية' } },
  approved: { bg: 'bg-green-100 text-green-800', icon: CheckCircle, label: { en: 'Approved', ar: 'موافق عليه' } },
  rejected: { bg: 'bg-red-100 text-red-800', icon: XCircle, label: { en: 'Rejected', ar: 'مرفوض' } },
  cancelled: { bg: 'bg-slate-100 text-slate-700', icon: Ban, label: { en: 'Withdrawn', ar: 'ملغى' } },
  deleted_by_manager: { bg: 'bg-rose-100 text-rose-800', icon: Ban, label: { en: 'Deleted by manager', ar: 'حذفه المدير' } },
};

// Only a request still awaiting a decision can be withdrawn.
const CANCELLABLE_STATUSES = ['pending_manager', 'pending_hr'];

const MyLeavePage = () => {
  const { token } = useAuth();
  const { isRTL, language } = useLanguage();

  // Owner's requirement (2026-11, item 4): every employee sees their own balance
  // - entitled, used, remaining - replacing the 2026-02-16 HR-only decision.
  const canSeeBalances = true;

  // State
  const [balances, setBalances] = useState([]);
  const [requests, setRequests] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // View dialog for timeline
  const [viewDialog, setViewDialog] = useState({ open: false, request: null });

  // Withdraw dialog
  const [cancelDialog, setCancelDialog] = useState({ open: false, request: null });
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState(null);

  // Create dialog
  const [createDialog, setCreateDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    leave_type_id: '',
    start_date: '',
    end_date: '',
    reason: ''
  });
  const [formError, setFormError] = useState(null);
  // Set when the request is longer than the remaining balance (server numbers).
  const [shortfall, setShortfall] = useState(null);
  const [exceptionsKey, setExceptionsKey] = useState(0);
  // Uploaded and stored the moment they are picked, so the form only carries
  // their IDs. Cleared with the form.
  const [attachments, setAttachments] = useState([]);

  const selectedLeaveType = leaveTypes.find(t => t.id === formData.leave_type_id);
  const attachmentRequired = Boolean(selectedLeaveType?.requires_attachment);

  // Fetch balances — HR only. Non-HR gets 403 from the API and we simply
  // skip the call to avoid a noisy console error.
  const fetchBalances = useCallback(async () => {
    if (!canSeeBalances) {
      setBalances([]);
      return;
    }
    try {
      const response = await axios.get(`${API_URL}/leave/balances/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBalances(response.data.balances || []);
    } catch (err) {
      console.error('Failed to fetch balances:', err);
    }
  }, [token, canSeeBalances]);

  // Fetch requests
  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/leave/requests/my?page=${page}&page_size=10`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRequests(response.data.requests || []);
      setTotal(response.data.total || 0);
      setTotalPages(response.data.total_pages || 1);
    } catch (err) {
      if (err.response?.status !== 400) {
        setError(err.response?.data?.detail?.error || 'Failed to load requests');
      }
    } finally {
      setLoading(false);
    }
  }, [token, page]);

  // Fetch leave types
  const fetchLeaveTypes = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/leave/types`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeaveTypes(response.data.leave_types || []);
    } catch (err) {
      console.error('Failed to fetch leave types:', err);
    }
  }, [token]);

  useEffect(() => {
    fetchBalances();
    fetchRequests();
    fetchLeaveTypes();
  }, [fetchBalances, fetchRequests, fetchLeaveTypes]);

  // Submit leave request
  const handleSubmit = async () => {
    if (!formData.leave_type_id || !formData.start_date || !formData.end_date || formData.reason.length < 10) {
      setFormError('Please fill all required fields (reason must be at least 10 characters)');
      return;
    }
    if (attachmentRequired && attachments.length === 0) {
      setFormError(isRTL
        ? 'هذا النوع من الإجازات يتطلب مرفقاً'
        : 'This leave type requires a supporting document');
      return;
    }

    try {
      setSubmitting(true);
      setFormError(null);
      setShortfall(null);

      await axios.post(`${API_URL}/leave/requests`, {
        ...formData,
        attachments: attachments.map(a => a.id),
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setCreateDialog(false);
      setFormData({ leave_type_id: '', start_date: '', end_date: '', reason: '' });
      setAttachments([]);
      fetchBalances();
      fetchRequests();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail?.code === 'INSUFFICIENT_BALANCE') {
        setShortfall(detail);
      } else {
        setFormError(detail?.error || 'Failed to submit leave request');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Withdraw a request that has not been decided yet. The reserved days come
  // straight back to the balance server-side.
  const handleCancel = async () => {
    const target = cancelDialog.request;
    if (!target) return;

    try {
      setCancelling(true);
      setCancelError(null);

      const query = cancelReason.trim() ? `?reason=${encodeURIComponent(cancelReason.trim())}` : '';
      await axios.post(`${API_URL}/leave/requests/${target.id}/cancel${query}`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setCancelDialog({ open: false, request: null });
      setCancelReason('');
      fetchBalances();
      fetchRequests();
    } catch (err) {
      setCancelError(err.response?.data?.detail?.error || 'Failed to withdraw this leave request');
    } finally {
      setCancelling(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(new Date(dateStr), 'PP');
    } catch {
      return dateStr;
    }
  };

  const getStatusStyle = (status) => STATUS_STYLES[status] || STATUS_STYLES.pending_manager;
  const getStatusLabel = (status) => {
    const style = getStatusStyle(status);
    return language === 'ar' ? style.label.ar : style.label.en;
  };

  // Deduplicate balances by leave_type_name
  const uniqueBalances = balances.reduce((acc, bal) => {
    if (!acc.find(b => b.leave_type_name === bal.leave_type_name)) {
      acc.push(bal);
    }
    return acc;
  }, []);

  return (
    <div className="space-y-6" data-testid="my-leave-page">
      {/* Header - responsive */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900">
            {isRTL ? 'إجازاتي' : 'My Leave'}
          </h1>
          <p className="text-sm sm:text-base text-slate-600 mt-1">
            {canSeeBalances
              ? (isRTL ? 'عرض الأرصدة وطلبات الإجازة' : 'View balances and leave requests')
              : (isRTL ? 'إرسال طلبات الإجازة وعرض الحالة' : 'Submit leave requests and view status')}
          </p>
        </div>
        <Button onClick={() => setCreateDialog(true)} data-testid="request-leave-btn" className="w-full sm:w-auto">
          <Plus className="h-4 w-4 me-2" />
          {isRTL ? 'طلب إجازة' : 'Request Leave'}
        </Button>
      </div>

      {/* Error */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
          <p className="text-red-700">{error}</p>
          <button onClick={() => setError(null)} className="ms-auto text-red-500 hover:text-red-700">×</button>
        </div>
      )}

      {/* Balance Cards - every employee sees their own (owner's requirement, 2026-11). */}
      {canSeeBalances && uniqueBalances.length > 0 && (
        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4"
          data-testid="my-leave-balance-grid"
        >
          {uniqueBalances.map((balance) => (
            <Card key={balance.id} className="bg-white border-slate-200" data-testid={`balance-card-${balance.leave_type_id}`}>
              <CardHeader className="pb-2">
                <CardTitle className="text-base sm:text-lg">
                  {language === 'ar' && balance.leave_type_name_ar ? balance.leave_type_name_ar : balance.leave_type_name}
                  {balance.leave_type_active === false && (
                    <span className="ms-2 text-xs font-normal text-slate-500" data-testid="balance-inactive">
                      {isRTL ? '(غير مفعّل — للاطلاع فقط)' : '(inactive — read-only)'}
                    </span>
                  )}
                </CardTitle>
                <CardDescription>{isRTL ? `السنة ${balance.year}` : `Year ${balance.year}`}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-end justify-between">
                  <div>
                    <p className="text-2xl sm:text-3xl font-bold text-slate-900">{balance.available_days}</p>
                    <p className="text-xs sm:text-sm text-slate-500">{isRTL ? 'الرصيد المتبقي' : 'days remaining'}</p>
                  </div>
                  <div className="text-end text-xs sm:text-sm text-slate-500">
                    <p>{isRTL ? 'المستحق:' : 'Entitled:'} {balance.allocated_days}</p>
                    <p>{isRTL ? 'المستخدم:' : 'Used:'} {balance.used_days}</p>
                    {balance.pending_days > 0 && (
                      <p className="text-amber-600">{isRTL ? 'معلق:' : 'Pending:'} {balance.pending_days}</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <MyBalanceExceptions isRTL={isRTL} refreshKey={exceptionsKey} />

      <MyReviewCertificates isRTL={isRTL} language={language} requests={requests} onSubmitted={fetchRequests} />

      {/* Requests Table */}
      <Card>
        <CardHeader>
          <CardTitle>{isRTL ? 'طلبات الإجازة' : 'Leave Requests'}</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          ) : requests.length === 0 ? (
            <div className="py-12 text-center">
              <Calendar className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">
                {isRTL ? 'لا توجد طلبات' : 'No Leave Requests'}
              </h3>
              <p className="text-slate-600 mb-4">
                {isRTL ? 'لم تقدم أي طلبات إجازة بعد' : "You haven't submitted any leave requests yet"}
              </p>
              <Button onClick={() => setCreateDialog(true)}>
                <Plus className="h-4 w-4 me-2" />
                {isRTL ? 'طلب إجازة' : 'Request Leave'}
              </Button>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table className="min-w-[600px]">
                  <TableHeader>
                    <TableRow>
                      <TableHead>{isRTL ? 'نوع الإجازة' : 'Leave Type'}</TableHead>
                      <TableHead>{isRTL ? 'من' : 'From'}</TableHead>
                      <TableHead>{isRTL ? 'إلى' : 'To'}</TableHead>
                      <TableHead>{isRTL ? 'الأيام' : 'Days'}</TableHead>
                      <TableHead>{isRTL ? 'الحالة' : 'Status'}</TableHead>
                      <TableHead className="hidden sm:table-cell">{isRTL ? 'تاريخ الطلب' : 'Submitted'}</TableHead>
                      <TableHead>{isRTL ? 'الإجراءات' : 'Actions'}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {requests.map((request) => {
                      const displayStatus = leaveDisplayStatus(request);
                      const statusStyle = getStatusStyle(displayStatus);
                      const StatusIcon = statusStyle.icon;
                      return (
                        <TableRow key={request.id}>
                          <TableCell className="font-medium">
                            {language === 'ar' && request.leave_type_name_ar ? request.leave_type_name_ar : request.leave_type_name}
                          </TableCell>
                          <TableCell>{formatDate(request.start_date)}</TableCell>
                          <TableCell>{formatDate(request.end_date)}</TableCell>
                          <TableCell>{request.working_days_requested}</TableCell>
                          <TableCell>
                            <Badge className={statusStyle.bg}>
                              <StatusIcon className="h-3 w-3 me-1" />
                              <span className="hidden xs:inline">{getStatusLabel(displayStatus)}</span>
                            </Badge>
                            {request.rejection_reason && (
                              <p className="text-xs text-red-600 mt-1 max-w-[150px] truncate">{request.rejection_reason}</p>
                            )}
                            {displayStatus === 'deleted_by_manager' && request.archive_reason && (
                              <p className="text-xs text-rose-700 mt-1 max-w-[150px] truncate" title={request.archive_reason} data-testid={`deleted-reason-${request.id}`}>{request.archive_reason}</p>
                            )}
                          </TableCell>
                          <TableCell className="text-slate-500 hidden sm:table-cell">{formatDate(request.submitted_at)}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setViewDialog({ open: true, request })}
                                data-testid={`view-leave-${request.id}`}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              {canCancelRequest(request, 'leave') && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  onClick={() => {
                                    setCancelReason('');
                                    setCancelError(null);
                                    setCancelDialog({ open: true, request });
                                  }}
                                  title={isRTL ? 'سحب الطلب' : 'Withdraw request'}
                                  data-testid={`cancel-leave-${request.id}`}
                                >
                                  <Ban className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t">
                  <p className="text-sm text-slate-600">
                    {isRTL ? `عرض ${requests.length} من ${total}` : `Showing ${requests.length} of ${total}`}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
                      {isRTL ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
                    </Button>
                    <span className="text-sm">{page} / {totalPages}</span>
                    <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
                      {isRTL ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Create Leave Request Dialog */}
      <Dialog open={createDialog} onOpenChange={setCreateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{isRTL ? 'طلب إجازة' : 'Request Leave'}</DialogTitle>
            <DialogDescription>
              {isRTL ? 'أدخل تفاصيل طلب الإجازة' : 'Enter leave request details'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {formError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
                {formError}
              </div>
            )}
            <InsufficientBalanceNotice
              shortfall={shortfall}
              form={formData}
              isRTL={isRTL}
              onRequested={() => {
                setShortfall(null);
                setCreateDialog(false);
                setExceptionsKey((k) => k + 1);
              }}
            />

            <div className="space-y-2">
              <Label>{isRTL ? 'نوع الإجازة' : 'Leave Type'} *</Label>
              <Select
                value={formData.leave_type_id}
                onValueChange={(v) => setFormData(prev => ({ ...prev, leave_type_id: v }))}
              >
                <SelectTrigger data-testid="leave-type-select">
                  <SelectValue placeholder={isRTL ? 'اختر نوع الإجازة' : 'Select leave type'} />
                </SelectTrigger>
                <SelectContent>
                  {leaveTypes.filter((t, i, arr) => arr.findIndex(x => x.name === t.name) === i).map(type => (
                    <SelectItem key={type.id} value={type.id}>
                      <span className="inline-flex items-center gap-1.5">
                        {language === 'ar' && type.name_ar ? type.name_ar : type.name}
                        {type.requires_attachment && <Paperclip className="h-3 w-3 text-slate-400" />}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{isRTL ? 'من تاريخ' : 'Start Date'} *</Label>
                <Input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
                  data-testid="start-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label>{isRTL ? 'إلى تاريخ' : 'End Date'} *</Label>
                <Input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
                  min={formData.start_date}
                  data-testid="end-date-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>{isRTL ? 'السبب' : 'Reason'} * ({isRTL ? '10 أحرف على الأقل' : 'min 10 characters'})</Label>
              <Textarea
                value={formData.reason}
                onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
                placeholder={isRTL ? 'أدخل سبب طلب الإجازة...' : 'Enter reason for leave request...'}
                rows={3}
                data-testid="reason-input"
              />
              {formData.reason.length > 0 && formData.reason.length < 10 && (
                <p className="text-sm text-red-500">
                  {isRTL ? `${10 - formData.reason.length} أحرف متبقية` : `${10 - formData.reason.length} more characters needed`}
                </p>
              )}
            </div>

            <LeaveAttachmentUploader
              token={token}
              value={attachments}
              onChange={setAttachments}
              isRTL={isRTL}
              required={attachmentRequired}
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialog(false)}>
              {isRTL ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={
                submitting
                || !formData.leave_type_id
                || !formData.start_date
                || !formData.end_date
                || formData.reason.length < 10
                || (attachmentRequired && attachments.length === 0)
              }
              data-testid="submit-leave-btn"
            >
              {submitting ? (
                <><Loader2 className="h-4 w-4 me-2 animate-spin" />{isRTL ? 'جاري الإرسال...' : 'Submitting...'}</>
              ) : (
                isRTL ? 'إرسال الطلب' : 'Submit Request'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Leave Request Dialog with Timeline */}
      <Dialog open={viewDialog.open} onOpenChange={(open) => !open && setViewDialog({ open: false, request: null })}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {viewDialog.request && (language === 'ar' && viewDialog.request.leave_type_name_ar ? viewDialog.request.leave_type_name_ar : viewDialog.request?.leave_type_name)}
            </DialogTitle>
            <DialogDescription>
              {isRTL ? 'تفاصيل طلب الإجازة ومسار الموافقة' : 'Leave Request Details & Approval Journey'}
            </DialogDescription>
          </DialogHeader>

          {viewDialog.request && (
            <Tabs defaultValue="timeline" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="timeline" data-testid="tab-timeline-leave">
                  {isRTL ? 'مسار الطلب' : 'Timeline'}
                </TabsTrigger>
                <TabsTrigger value="details" data-testid="tab-details-leave">
                  {isRTL ? 'التفاصيل' : 'Details'}
                </TabsTrigger>
              </TabsList>
              
              {/* Timeline Tab */}
              <TabsContent value="timeline" className="mt-4">
                <RequestTimeline
                  request={viewDialog.request}
                  type="leave"
                  language={language}
                  isRTL={isRTL}
                />
              </TabsContent>
              
              {/* Details Tab */}
              <TabsContent value="details" className="mt-4 space-y-4">
                {/* Status */}
                <div className="flex items-center justify-between">
                  <span className="text-slate-600">{isRTL ? 'الحالة:' : 'Status:'}</span>
                  <Badge className={getStatusStyle(viewDialog.request.status).bg}>
                    {getStatusLabel(viewDialog.request.status)}
                  </Badge>
                </div>

                {/* Leave Type */}
                <div className="flex items-center justify-between">
                  <span className="text-slate-600">{isRTL ? 'نوع الإجازة:' : 'Leave Type:'}</span>
                  <span className="font-medium">
                    {language === 'ar' && viewDialog.request.leave_type_name_ar ? viewDialog.request.leave_type_name_ar : viewDialog.request.leave_type_name}
                  </span>
                </div>

                {/* Date Range */}
                <div className="bg-slate-50 p-3 rounded space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-600">{isRTL ? 'من:' : 'From:'}</span>
                    <span className="font-medium">{formatDate(viewDialog.request.start_date)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-600">{isRTL ? 'إلى:' : 'To:'}</span>
                    <span className="font-medium">{formatDate(viewDialog.request.end_date)}</span>
                  </div>
                  <div className="flex items-center justify-between border-t pt-2">
                    <span className="text-slate-600">{isRTL ? 'إجمالي الأيام:' : 'Total Days:'}</span>
                    <span className="font-medium">{viewDialog.request.working_days_requested}</span>
                  </div>
                </div>

                {/* Submitted */}
                <div className="flex items-center justify-between">
                  <span className="text-slate-600">{isRTL ? 'تاريخ التقديم:' : 'Submitted:'}</span>
                  <span>{formatDate(viewDialog.request.submitted_at)}</span>
                </div>

                {/* Reason */}
                {viewDialog.request.reason && (
                  <div className="space-y-2">
                    <h4 className="font-medium">{isRTL ? 'السبب:' : 'Reason:'}</h4>
                    <p className="text-sm text-slate-600 bg-slate-50 p-3 rounded">{viewDialog.request.reason}</p>
                  </div>
                )}

                {/* Rejection Reason */}
                {viewDialog.request.rejection_reason && (
                  <div className="space-y-2">
                    <h4 className="font-medium text-red-600">{isRTL ? 'سبب الرفض:' : 'Rejection Reason:'}</h4>
                    <p className="text-sm text-red-600 bg-red-50 p-3 rounded">{viewDialog.request.rejection_reason}</p>
                  </div>
                )}

                {/* Attachments - removable only while nothing has been decided */}
                <LeaveAttachmentList
                  token={token}
                  requestId={viewDialog.request.id}
                  isRTL={isRTL}
                  canRemove={CANCELLABLE_STATUSES.includes(viewDialog.request.status)}
                  onChanged={fetchRequests}
                />
              </TabsContent>
            </Tabs>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setViewDialog({ open: false, request: null })}>
              {isRTL ? 'إغلاق' : 'Close'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Withdraw confirmation */}
      <Dialog
        open={cancelDialog.open}
        onOpenChange={(open) => !open && setCancelDialog({ open: false, request: null })}
      >
        <DialogContent data-testid="cancel-leave-dialog">
          <DialogHeader>
            <DialogTitle>{isRTL ? 'سحب طلب الإجازة' : 'Withdraw leave request'}</DialogTitle>
            <DialogDescription>
              {isRTL
                ? 'سيتم إرجاع الأيام المحجوزة إلى رصيدك فوراً. لا يمكن التراجع عن هذا الإجراء.'
                : 'The reserved days go straight back to your balance. This cannot be undone.'}
            </DialogDescription>
          </DialogHeader>

          {cancelDialog.request && (
            <div className="space-y-4">
              <div className="rounded-lg border bg-slate-50 p-3 text-sm space-y-1">
                <div className="flex justify-between">
                  <span className="text-slate-600">{isRTL ? 'نوع الإجازة' : 'Leave type'}</span>
                  <span className="font-medium">
                    {language === 'ar' && cancelDialog.request.leave_type_name_ar
                      ? cancelDialog.request.leave_type_name_ar
                      : cancelDialog.request.leave_type_name}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">{isRTL ? 'الفترة' : 'Dates'}</span>
                  <span className="font-medium">
                    {formatDate(cancelDialog.request.start_date)} – {formatDate(cancelDialog.request.end_date)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">{isRTL ? 'الأيام المعادة' : 'Days returned'}</span>
                  <span className="font-medium" data-testid="cancel-leave-days">
                    {cancelDialog.request.working_days_requested}
                  </span>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="cancel-reason">
                  {isRTL ? 'ملاحظة (اختياري)' : 'Note (optional)'}
                </Label>
                <Textarea
                  id="cancel-reason"
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  maxLength={500}
                  placeholder={isRTL ? 'سبب السحب...' : 'Why are you withdrawing this request?'}
                  data-testid="cancel-leave-reason"
                />
              </div>

              {cancelError && (
                <p className="text-sm text-red-600" data-testid="cancel-leave-error">{cancelError}</p>
              )}
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCancelDialog({ open: false, request: null })}
              data-testid="cancel-leave-dismiss"
            >
              {isRTL ? 'إلغاء' : 'Keep request'}
            </Button>
            <Button
              variant="destructive"
              onClick={handleCancel}
              disabled={cancelling}
              data-testid="cancel-leave-confirm"
            >
              {cancelling && <Loader2 className="h-4 w-4 me-2 animate-spin" />}
              {isRTL ? 'تأكيد السحب' : 'Withdraw request'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyLeavePage;
